import { RefObject, useCallback, useEffect, useRef, useState } from "react";

export type UseInfiniteRowsOptions = {
  /** The TRUE total row count this table claims to have — shown in the
   * header/count badge, not just "however many happen to be loaded so
   * far". */
  totalCount: number;
  /** Rows appended per "page" once the sentinel scrolls into view. */
  pageSize?: number;
  /** Rows visible on first render, before any scrolling. Defaults to `pageSize`. */
  initialCount?: number;
  /** Simulated network delay (ms) before the next page appends — this app
   * has no real backend, so this is purely for a believable loading-row
   * UX, not an actual fetch. */
  delayMs?: number;
  /** The actual scrolling element (from `useScrollEdges`'s `ref`) — the
   * IntersectionObserver watches the sentinel against THIS as its root,
   * not the viewport, since these tables scroll within a contained div. */
  scrollRootRef: RefObject<HTMLElement | null>;
};

/**
 * Infinite-scroll row count for a contained-scroll table: starts at
 * `initialCount`, and grows by `pageSize` (after a simulated `delayMs`
 * fetch) whenever a sentinel element — rendered as the last row, see
 * `<SkeletonRow>` / the sentinel `ref` this returns — scrolls into view of
 * `scrollRootRef`'s element. Pair with `<SkeletonRow>` rows shown while
 * `isLoadingMore` is true.
 */
export function useInfiniteRows({
  totalCount,
  pageSize = 20,
  initialCount,
  delayMs = 500,
  scrollRootRef,
}: UseInfiniteRowsOptions) {
  const [loadedCount, setLoadedCount] = useState(() =>
    Math.min(initialCount ?? pageSize, totalCount),
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const triggerLoad = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setIsLoadingMore(true);
  }, []);

  // A CALLBACK ref, not a plain `useRef` + `useEffect` — the sentinel is
  // rendered conditionally (only once the caller's own initial-mount
  // skeleton is gone AND `hasMore` is true), so it can mount well after
  // this hook's first render, or unmount and remount as `hasMore` flips.
  // A `useEffect([])`-style setup only runs when ITS OWN deps change, which
  // has nothing to do with when the sentinel DOM node actually appears —
  // that earlier approach would see `sentinelRef.current === null` on the
  // one run that happened to fire (e.g. during the initial-loading skeleton
  // phase) and never retry once the real sentinel mounted, silently
  // stalling `hasMore` at true forever. A callback ref fires exactly when
  // React attaches/detaches the DOM node, which is the actual event this
  // needs to react to.
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;

      const root = scrollRootRef.current;
      if (!root) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) triggerLoad();
        },
        { root, rootMargin: "300px" },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollRootRef, triggerLoad, loadedCount],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  useEffect(() => {
    if (!isLoadingMore) return;
    const timer = setTimeout(() => {
      setLoadedCount((count) => Math.min(count + pageSize, totalCount));
      setIsLoadingMore(false);
      loadingRef.current = false;
    }, delayMs);
    return () => clearTimeout(timer);
  }, [isLoadingMore, pageSize, totalCount, delayMs]);

  return {
    loadedCount,
    isLoadingMore,
    hasMore: loadedCount < totalCount,
    sentinelRef,
  };
}
